import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import {
  AlertTriangle,
  Banknote,
  Building2,
  ChevronDown,
  Gavel,
  ShieldCheck,
  ShoppingCart,
  Users,
} from 'lucide-react';
import { fetchPlans } from '../../shared/api/endpoints';
import { PlanCards } from '../pricing/PlanCards';
import type { PlanDto } from '@foco/contracts';

/**
 * The landing page (section 11).
 *
 * Order of sections is fixed by the specification and is followed exactly:
 * hero with a working search box · what we check · how it works · data sources ·
 * pricing · FAQ · for developers · footer (in the layout).
 *
 * The one thing worth stating about the content: the "what we check" cards
 * describe only what can be verified against a connected source. There is no
 * card for court cases, enforcement or bankruptcy, because the customer removed
 * those from the product — and a marketing card for a feature that does not
 * exist is the fastest way to lose a customer who signs up expecting it.
 */

const WHAT_WE_CHECK = [
  { key: 'registration', Icon: Building2 },
  { key: 'persons', Icon: Users },
  { key: 'sanctions', Icon: ShieldCheck },
  { key: 'procurement', Icon: ShoppingCart },
  { key: 'debt', Icon: Banknote },
  { key: 'risk', Icon: AlertTriangle },
] as const;

export function LandingPage(): React.ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const plans = useQuery({
    queryKey: ['plans'],
    queryFn: ({ signal }) => fetchPlans({ signal }),
    staleTime: 5 * 60 * 1000,
  });

  const onSearch = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = query.trim();
    if (trimmed.length >= 2) navigate(`/search?q=${encodeURIComponent(trimmed)}`);
  };

  return (
    <>
      {/* 2. Hero. The search box works signed-out on the free plan. */}
      <section className="border-b border-[var(--color-border)] bg-gradient-to-b from-[var(--color-surface-2)] to-[var(--color-bg)]">
        <div className="mx-auto max-w-4xl px-4 py-14 text-center sm:py-20">
          <h1 className="text-3xl font-extrabold tracking-tight sm:text-5xl">
            {t('landing.hero.title')}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base text-[var(--color-muted-fg)] sm:text-lg">
            {t('landing.hero.subtitle')}
          </p>

          <form onSubmit={onSearch} role="search" className="mx-auto mt-8 flex max-w-2xl gap-2">
            <label className="sr-only" htmlFor="hero-search">
              {t('search.label')}
            </label>
            <input
              id="hero-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('search.placeholder')}
              autoComplete="off"
              className="h-12 flex-1 rounded-[var(--radius-control)] border border-[var(--color-border)] bg-[var(--color-surface)] px-4 text-base outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/30"
            />
            <button
              type="submit"
              className="h-12 shrink-0 rounded-[var(--radius-control)] bg-brand-700 px-5 font-semibold text-white transition hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-600/40"
            >
              {t('search.submit')}
            </button>
          </form>

          <p className="mt-3 text-xs text-[var(--color-muted-fg)]">{t('search.hint')}</p>
          <p className="mt-6 text-sm text-[var(--color-muted-fg)]">
            {t('landing.hero.trustLine')}
          </p>
        </div>
      </section>

      {/* 3. What we check — six cards, honestly scoped. */}
      <section className="mx-auto max-w-6xl px-4 py-14">
        <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
          {t('landing.whatWeCheck.title')}
        </h2>
        <p className="mt-2 text-[var(--color-muted-fg)]">{t('landing.whatWeCheck.subtitle')}</p>

        <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {WHAT_WE_CHECK.map(({ key, Icon }) => (
            <li
              key={key}
              className="rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface)] p-5 shadow-[var(--shadow-card)]"
            >
              <Icon aria-hidden className="h-6 w-6 text-brand-600" />
              <h3 className="mt-3 font-semibold">{t(`landing.whatWeCheck.cards.${key}.title`)}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-[var(--color-muted-fg)]">
                {t(`landing.whatWeCheck.cards.${key}.body`)}
              </p>
            </li>
          ))}
        </ul>

        {/* Naming the gaps is a feature: it sets expectations before payment and
            it is what section 3.5 requires of the coverage matrix. */}
        <div className="mt-8 rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface-2)] p-5">
          <h3 className="flex items-center gap-2 font-semibold">
            <Gavel aria-hidden className="h-5 w-5 text-[var(--color-muted-fg)]" />
            {t('landing.whatWeCheck.excluded.title')}
          </h3>
          <p className="mt-2 text-sm text-[var(--color-muted-fg)]">
            {t('landing.whatWeCheck.excluded.body')}
          </p>
        </div>
      </section>

      {/* 4. How it works. */}
      <section className="border-y border-[var(--color-border)] bg-[var(--color-surface-2)]">
        <div className="mx-auto max-w-6xl px-4 py-14">
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
            {t('landing.howItWorks.title')}
          </h2>
          <ol className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {(['one', 'two', 'three', 'four'] as const).map((step, index) => (
              <li key={step}>
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-700 text-sm font-bold text-white">
                  {index + 1}
                </div>
                <h3 className="mt-3 font-semibold">{t(`landing.howItWorks.steps.${step}.title`)}</h3>
                <p className="mt-1 text-sm text-[var(--color-muted-fg)]">
                  {t(`landing.howItWorks.steps.${step}.body`)}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* 5. Data sources, with the coverage gaps stated. */}
      <section className="mx-auto max-w-6xl px-4 py-14">
        <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
          {t('landing.dataSources.title')}
        </h2>
        <p className="mt-2 max-w-3xl text-[var(--color-muted-fg)]">
          {t('landing.dataSources.subtitle')}
        </p>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {(['brazil', 'argentina', 'sanctions', 'chile', 'colombia', 'others'] as const).map(
            (group) => (
              <div
                key={group}
                className="rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface)] p-5"
              >
                <h3 className="font-semibold">{t(`landing.dataSources.groups.${group}.title`)}</h3>
                <p className="mt-1 text-sm text-[var(--color-muted-fg)]">
                  {t(`landing.dataSources.groups.${group}.body`)}
                </p>
              </div>
            ),
          )}
        </div>
        <p className="mt-6 text-sm text-[var(--color-muted-fg)]">
          <Link to="/status" className="text-brand-600 hover:underline">
            {t('landing.dataSources.statusLink')}
          </Link>
        </p>
      </section>

      {/* 6. Pricing. */}
      <section className="border-y border-[var(--color-border)] bg-[var(--color-surface-2)]">
        <div className="mx-auto max-w-6xl px-4 py-14">
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
            {t('landing.pricing.title')}
          </h2>
          <p className="mt-2 text-[var(--color-muted-fg)]">{t('landing.pricing.subtitle')}</p>

          {plans.isLoading ? (
            <p className="mt-8 text-sm text-[var(--color-muted-fg)]">{t('common.loading')}</p>
          ) : plans.isError ? (
            // The prices are in the API config; if it is unreachable the page
            // says so rather than showing invented numbers.
            <p className="mt-8 text-sm text-[var(--color-muted-fg)]">
              {t('landing.pricing.unavailable')}
            </p>
          ) : (
            <PlanCards plans={plans.data as PlanDto[]} />
          )}
        </div>
      </section>

      {/* 7. FAQ — including the honest answer about coverage. */}
      <section className="mx-auto max-w-3xl px-4 py-14">
        <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">{t('landing.faq.title')}</h2>
        <div className="mt-6 divide-y divide-[var(--color-border)] border-y border-[var(--color-border)]">
          {(['lessData', 'sources', 'courts', 'privacy', 'refund'] as const).map((item) => (
            <details key={item} className="group py-4">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-medium">
                {t(`landing.faq.items.${item}.question`)}
                <ChevronDown
                  aria-hidden
                  className="h-4 w-4 shrink-0 text-[var(--color-muted-fg)] transition group-open:rotate-180"
                />
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-[var(--color-muted-fg)]">
                {t(`landing.faq.items.${item}.answer`)}
              </p>
            </details>
          ))}
        </div>
      </section>

      {/* 8. For developers. */}
      <section className="border-t border-[var(--color-border)] bg-[var(--color-surface-2)]">
        <div className="mx-auto max-w-6xl px-4 py-14">
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
            {t('landing.developers.title')}
          </h2>
          <p className="mt-2 max-w-2xl text-[var(--color-muted-fg)]">
            {t('landing.developers.subtitle')}
          </p>

          <pre className="mt-6 overflow-x-auto rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-xs leading-relaxed">
            <code>{`curl -H "Authorization: Bearer foco_..." \\
  https://foco.arriba.digital/v1/entity/br/19131243000197`}</code>
          </pre>

          <Link
            to="/docs/api"
            className="mt-6 inline-block rounded-[var(--radius-control)] border border-[var(--color-border)] px-4 py-2 text-sm font-medium hover:border-brand-600 hover:text-brand-600"
          >
            {t('landing.developers.cta')}
          </Link>
        </div>
      </section>
    </>
  );
}
